import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PublicDashboardResponse } from '../../shared/types';
import { login } from '../api';
import './Home.css';

const vertexShaderSource = `
  attribute vec2 a_position;
  varying vec2 vUv;

  void main() {
    vUv = (a_position + 1.0) * 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

const fragmentShaderSource = `
  precision highp float;

  uniform float u_time;
  uniform vec2 u_resolution;
  varying vec2 vUv;

  float random(vec2 st) {
    return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
  }

  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }

  float snoise(vec2 v) {
    const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
    vec2 i = floor(v + dot(v, C.yy));
    vec2 x0 = v - i + dot(i, C.xx);
    vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy -= i1;
    i = mod289(i);
    vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
    vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
    m = m * m;
    m = m * m;
    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
    vec3 g;
    g.x = a0.x * x0.x + h.x * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 130.0 * dot(m, g);
  }

  void main() {
    vec2 st = gl_FragCoord.xy / u_resolution.xy;
    vec2 uv = st;
    uv.x *= u_resolution.x / u_resolution.y;

    float foldAngle = 0.8;
    vec2 foldDir = vec2(cos(foldAngle), sin(foldAngle));
    float foldDist = dot(uv, foldDir) - 0.5;
    float n = snoise(uv * 1.5 + u_time * 0.1) * 0.5;
    float intensity = smoothstep(-0.2, 0.8, foldDist + n * 0.5);
    float shadow = 1.0 - smoothstep(0.0, 0.1, abs(foldDist + n * 0.2)) * 0.5;
    intensity *= shadow;

    vec3 color1 = vec3(0.02, 0.10, 0.15);
    vec3 color2 = vec3(0.83, 0.68, 0.21);
    vec3 color3 = vec3(0.98, 0.92, 0.84);
    vec3 finalColor = mix(color1, color2, intensity);
    finalColor = mix(finalColor, color3, smoothstep(0.6, 1.0, intensity));

    float grain = random(st * (u_time * 0.01 + 1.0));
    finalColor += (grain - 0.5) * 0.12;
    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

const ROTATING_ENDINGS = ['Magazine', 'Newsletter', 'X Account', 'Technical Journal', 'Newspaper'];
const PIPELINE: Array<[keyof PublicDashboardResponse['pipeline'], string]> = [
  ['messages', 'Messages'],
  ['signal', 'Signal'],
  ['ideas', 'Ideas'],
  ['drafts', 'Drafts'],
  ['published', 'Published'],
];
const QUEUE: Array<[keyof PublicDashboardResponse['queue'], string]> = [
  ['incubating', 'Incubating'],
  ['needs_approval', 'Review'],
  ['scheduled', 'Scheduled'],
  ['published', 'Published'],
];
const formatCount = new Intl.NumberFormat('en-US');

function createShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function SingularityField() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl', { antialias: false, alpha: false });
    if (!gl) return;

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!vertexShader || !fragmentShader) return;

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );
    const position = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const timeUniform = gl.getUniformLocation(program, 'u_time');
    const resolutionUniform = gl.getUniformLocation(program, 'u_resolution');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const startedAt = performance.now();
    let frame = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    const draw = (now: number) => {
      gl.uniform1f(timeUniform, reducedMotion ? 0 : (now - startedAt) / 1000);
      gl.uniform2f(resolutionUniform, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      frame = requestAnimationFrame(draw);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    frame = requestAnimationFrame(draw);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
    };
  }, []);

  return <canvas ref={ref} className="public-background" aria-hidden="true" />;
}

function LoginModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const close = useCallback(() => {
    setPassword('');
    setError(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    inputRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [close, open]);

  if (!open) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="public-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) close();
      }}
    >
      <section className="public-login-modal" role="dialog" aria-modal="true" aria-labelledby="login-title">
        <button className="public-modal-close" type="button" onClick={close} aria-label="Close login">
          ×
        </button>
        <h2 id="login-title">Login</h2>
        <form onSubmit={submit}>
          <label htmlFor="public-password">Password</label>
          <input
            ref={inputRef}
            id="public-password"
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
          />
          <button type="submit" disabled={!password || busy}>
            {busy ? 'Opening…' : 'Open application'}
          </button>
          {error ? <p className="public-login-error">{error}</p> : null}
        </form>
      </section>
    </div>
  );
}

function PublicDashboard() {
  const [now, setNow] = useState(() => new Date());
  const [dashboard, setDashboard] = useState<PublicDashboardResponse | null>(null);

  useEffect(() => {
    const clock = window.setInterval(() => setNow(new Date()), 1000);
    const load = () => {
      void fetch('/api/public-dashboard')
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error('unavailable'))))
        .then((data: PublicDashboardResponse) => setDashboard(data))
        .catch(() => undefined);
    };
    load();
    const refresh = window.setInterval(load, 60_000);
    return () => {
      window.clearInterval(clock);
      window.clearInterval(refresh);
    };
  }, []);

  const utc = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(now);
  const pipelineMax = Math.max(1, ...PIPELINE.map(([key]) => dashboard?.pipeline[key] ?? 0));
  const signal = dashboard?.signal ?? [];
  const messageTotal = signal.reduce((total, point) => total + point.messages, 0);
  const signalTotal = signal.reduce((total, point) => total + point.signal, 0);
  const signalRate = messageTotal > 0 ? Math.round((signalTotal / messageTotal) * 100) : 0;
  const maxDailyMessages = Math.max(1, ...signal.map((point) => point.messages));
  const chartStep = signal.length > 1 ? 1000 / (signal.length - 1) : 1000;
  const signalPoints = signal.length > 0
    ? signal.map((point, index) => {
        const ratio = point.messages > 0 ? Math.min(1, point.signal / point.messages) : 0;
        return `${index * chartStep},${110 - ratio * 100}`;
      }).join(' ')
    : '0,110 1000,110';
  const firstSignalDay = signal[0]?.date.slice(5).replace('-', '.') ?? '—';
  const lastSignalDay = signal.at(-1)?.date.slice(5).replace('-', '.') ?? '—';

  return (
    <div className="public-ledger" aria-busy={!dashboard}>
      <div className="public-ledger-inner">
        <header className="public-ledger-head">
          <div className="public-ledger-wordmark">Application</div>
          <p className="public-ledger-description">Humans in a group chat think and discuss freely. The Tentacle application runs 24/7 ingesting, mining, synthesizing, researching, editing, and scheduling the group's most valuable insights to the exact editorial requirements of their publishing destinations.</p>
          <div className="public-ledger-rule" />
        </header>

        <div className="public-ledger-grid">
          <section className="public-widget public-widget-small">
            <div className="public-widget-head"><span>Idea Queue</span><span>Current</span></div>
            <dl className="public-kv-list">
              {QUEUE.map(([key, label]) => (
                <div key={key}><dt>{label}</dt><dd>{dashboard ? formatCount.format(dashboard.queue[key]) : '—'}</dd></div>
              ))}
            </dl>
          </section>

          <section className="public-widget public-widget-wide">
            <div className="public-widget-head"><span>Publishing Pipeline</span><span>Last 30 Days</span></div>
            <div className="public-ascii-chart">
              {PIPELINE.map(([key, label]) => {
                const value = dashboard?.pipeline[key] ?? 0;
                const blocks = dashboard ? Math.round((value / pipelineMax) * 20) : 0;
                return (
                  <div className="public-ascii-row" key={key}>
                    <span>{label}</span>
                    <span aria-hidden="true">{'█'.repeat(blocks)}{'░'.repeat(20 - blocks)}</span>
                    <strong>{dashboard ? formatCount.format(value) : '—'}</strong>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="public-widget public-widget-full">
            <div className="public-widget-head"><span>Group Signal</span><span>{dashboard ? `${signalRate}% Signal` : '—'}</span></div>
            <div className="public-signal-chart">
              <svg viewBox="0 0 1000 120" preserveAspectRatio="none" role="img" aria-label="Fourteen days of group messages and messages selected as idea material">
                <line x1="0" y1="30" x2="1000" y2="30" />
                <line x1="0" y1="60" x2="1000" y2="60" />
                <line x1="0" y1="90" x2="1000" y2="90" />
                <g className="public-signal-noise">
                  {signal.map((point, index) => {
                    const slot = 1000 / Math.max(signal.length, 1);
                    const height = (point.messages / maxDailyMessages) * 96;
                    return <rect key={point.date} x={index * slot + slot * 0.2} y={110 - height} width={slot * 0.6} height={height} />;
                  })}
                </g>
                <polyline points={signalPoints} />
              </svg>
            </div>
            <div className="public-ledger-meta public-signal-meta">
              <span>{firstSignalDay}</span>
              <span>{dashboard ? `${formatCount.format(signalTotal)} SIGNAL / ${formatCount.format(messageTotal)} MESSAGES` : '—'}</span>
              <span>{lastSignalDay}</span>
            </div>
          </section>

          <section className="public-widget public-widget-half">
            <div className="public-widget-head"><span>Idea Cluster Matrix</span><span>{dashboard ? `${dashboard.ideas.length} Recent` : '—'}</span></div>
            <div className="public-matrix">
              {dashboard?.ideas.map((idea, index) => {
                const level = idea.score == null ? 0 : Math.max(1, Math.min(4, Math.ceil(idea.score / 2.5)));
                const label = `${idea.kind}, ${idea.stage.replace('_', ' ')}, ${idea.score == null ? 'unscored' : `score ${idea.score}`}`;
                return <i className={`level-${level} stage-${idea.stage}`} aria-label={label} title={label} key={`${index}-${idea.kind}-${idea.stage}`} />;
              })}
            </div>
          </section>

          <section className="public-widget public-widget-half">
            <div className="public-widget-head"><span>Event Stream</span><span>{utc} UTC</span></div>
            <table className="public-event-table">
              <thead><tr><th>Time</th><th>Event</th><th>State</th></tr></thead>
              <tbody>
                {dashboard?.events.map((event, index) => (
                  <tr key={`${event.startedAt}-${event.agentSlug}-${index}`}>
                    <td>{event.startedAt.slice(11, 19)}</td>
                    <td>{event.agentSlug.toUpperCase().replace(/-/g, '_')}_RUN</td>
                    <td>{event.status.toUpperCase()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [loginOpen, setLoginOpen] = useState(false);
  const [ending, setEnding] = useState(0);
  const closeLogin = useCallback(() => setLoginOpen(false), []);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(
      () => setEnding((current) => (current + 1) % ROTATING_ENDINGS.length),
      3200,
    );
    return () => window.clearInterval(timer);
  }, []);

  return (
    <main className="public-page" id="top">
      <SingularityField />

      <header className="public-nav">
        <a className="public-nav-mark" href="#top">Tentacle</a>
        <nav aria-label="Public navigation">
          <a href="#studio">Studio</a>
          <a href="#application">Application</a>
          <button type="button" onClick={() => setLoginOpen(true)}>Login</button>
        </nav>
      </header>

      <section className="public-hero" aria-labelledby="public-title">
        <div>
          <h1 id="public-title">The Intelligence Layer for Publishing Collectives</h1>
          <p className="public-hero-rotation">Turn your group chat into a <span key={ROTATING_ENDINGS[ending]}>{ROTATING_ENDINGS[ending]}</span></p>
        </div>
      </section>

      <section className="public-studio" id="studio" aria-labelledby="studio-title">
        <div>
          <h2 id="studio-title">Tentacle Studio</h2>
          <p>Tentacle is an experimental studio building Tentacle, a collective intelligence layer for publishing collectives. We also consult and build tentacles for other organizations, using Tentacle. Optionally, your tentacle can plug into the network of tentacles, or remain fully independent.</p>
        </div>
      </section>

      <section className="public-application" id="application" aria-label="Application">
        <PublicDashboard />
      </section>

      <LoginModal open={loginOpen} onClose={closeLogin} />
    </main>
  );
}
